import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { forwardRef } from 'react'

import { cn } from 'utils/classMerge'
import Text from 'components/ui/text'

const buttonVariants = cva(
  'tw-font-primary tw-relative tw-m-0 tw-flex tw-appearance-none tw-flex-col tw-items-center tw-justify-center tw-whitespace-pre-wrap tw-rounded-lg tw-border-none tw-bg-transparent tw-p-0 tw-transition-all tw-duration-300 disabled:tw-cursor-not-allowed md:tw-flex-row',
  {
    variants: {
      variant: {
        default: '',
        'default-only-lg': '',
        button:
          'tw-rounded-none tw-border-b-0 tw-border-l tw-border-r tw-border-t-0 !tw-border-solid tw-border-black/10',
        control: '',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)
interface TabButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  ({ className, variant, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'

    const buttonComponent = (
      <Comp
        type="button"
        className={cn(buttonVariants({ variant }), className)}
        ref={ref}
        {...props}
      >
        <Text
          renderChildren
          as="div"
          className="tw-flex tw-items-center tw-justify-center tw-gap-2"
        >
          {children}
        </Text>
      </Comp>
    )

    return typeof children === 'string' ? (
      <Text asChild skipTranslation>
        {buttonComponent}
      </Text>
    ) : (
      buttonComponent
    )
  },
)

TabButton.displayName = 'Button'

export { TabButton }
